import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIcon from '@mui/icons-material/Phone';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { toast } from 'react-toastify';

import AuthContext from '../../context/authContext';
import useInternalBooking from '../../hooks/useInternalBooking';

dayjs.locale('de');

const WEEKDAY_SHORT = { 1: 'Mo', 2: 'Di', 3: 'Mi', 4: 'Do', 5: 'Fr', 6: 'Sa', 7: 'So' };

const VARIETY_LABELS = {
  margherita: 'Margherita',
  salami: 'Salami',
  halfHalf: 'Halb/Halb',
};

const OUTREACH_STATUS = {
  open: { label: 'Anrufen', color: 'warning' },
  notReached: { label: 'Nicht erreicht', color: 'error' },
  infoMailSent: { label: 'Infomail gesendet', color: 'info' },
  reached: { label: 'Erreicht', color: 'info' },
  booked: { label: 'Pizza gebucht', color: 'success' },
  declined: { label: 'Keine Pizza gewünscht', color: 'default' },
};

// "Do 12:00" from a weekly moment ({weekday: ISO 1-7, hour}).
const formatWeeklyMoment = moment => {
  if (!moment) return '–';
  const day = WEEKDAY_SHORT[moment.weekday] || '';
  const hour = String(moment.hour ?? 0).padStart(2, '0');
  return `${day} ${hour}:00`.trim();
};

// "ab 6 Pers. 1 Pizza, ab 11 Pers. 2, ab 21 Pers. 3 – ohne Aufpreis"
const formatTierInfo = tiers => {
  if (!Array.isArray(tiers) || tiers.length === 0) return null;
  const parts = tiers.map((tier, index) =>
    index === 0
      ? `ab ${tier.fromPersons} Pers. ${tier.pizzaCount} ${tier.pizzaCount === 1 ? 'Pizza' : 'Pizzen'}`
      : `ab ${tier.fromPersons} Pers. ${tier.pizzaCount}`
  );
  return `${parts.join(', ')} – ohne Aufpreis`;
};

// "2× Pizza Salami (Margherita, Salami), 1× Pizzablech"
const formatItems = items =>
  (items || [])
    .map(item => {
      const varieties = (item.varieties || []).map(value => VARIETY_LABELS[value] || value).join(', ');
      return `${item.count}× ${item.name}${varieties ? ` (${varieties})` : ''}`;
    })
    .join(', ');

const formatCountdown = (target, nowTs) => {
  const diffMs = new Date(target).getTime() - nowTs;
  if (!Number.isFinite(diffMs)) return '';
  if (diffMs <= 0) return 'abgelaufen';
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) return `noch ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `noch ${hours} Std.`;
  const days = Math.floor(hours / 24);
  return `noch ${days} ${days === 1 ? 'Tag' : 'Tage'}`;
};

const PreorderSkeleton = () => (
  <Stack spacing={2}>
    {[0, 1, 2].map(key => (
      <Skeleton key={key} variant='rounded' height={140} />
    ))}
  </Stack>
);

// One call task for a KiBu booking without pizza.
const OutreachTaskCard = ({
  task,
  canUpdate,
  busy,
  onOpenBookDialog,
  onOpenNotReachedDialog,
  onSendInfoMail,
  onClose,
  onReopen,
}) => {
  const [attemptsOpen, setAttemptsOpen] = useState(false);
  const status = task.outreach?.status || 'open';
  const statusConfig = OUTREACH_STATUS[status] || OUTREACH_STATUS.open;
  const attempts = Array.isArray(task.outreach?.attempts) ? task.outreach.attempts : [];
  const statusLabel =
    status === 'infoMailSent' && task.outreach?.infoMailSentAt
      ? `${statusConfig.label} ${dayjs(task.outreach.infoMailSentAt).format('DD.MM. HH:mm')}`
      : statusConfig.label;
  const showBook = ['open', 'notReached', 'infoMailSent', 'reached'].includes(status);
  const showNotReached = ['open', 'notReached', 'infoMailSent'].includes(status);
  const showInfoMail = status === 'notReached';
  const showClose = !['booked', 'declined'].includes(status);
  const showReopen = status === 'declined';

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: 'divider' }}>
      <Stack spacing={1.25}>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
          <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
            {dayjs(task.startTime).format('dd DD.MM. HH:mm')} · {task.customerName}
          </Typography>
          <Chip size='small' variant='outlined' label={`#${task.bookingNumber}`} />
          <Chip size='small' label={`${task.participantCount} Personen`} />
          <Chip size='small' color={statusConfig.color} label={statusLabel} />
        </Stack>

        <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap' useFlexGap>
          {task.phone ? (
            <Link
              href={`tel:${task.phone}`}
              underline='hover'
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}
            >
              <PhoneIcon fontSize='small' />
              {task.phone}
            </Link>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              Keine Telefonnummer hinterlegt
            </Typography>
          )}
          {task.selfBookUntil && (
            <Typography variant='body2' color='text.secondary'>
              Selbstbuchung bis {dayjs(task.selfBookUntil).format('dd DD.MM. HH:mm')}
            </Typography>
          )}
        </Stack>

        {attempts.length > 0 && (
          <Box>
            <Button
              size='small'
              color='inherit'
              onClick={() => setAttemptsOpen(prev => !prev)}
              endIcon={attemptsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none', color: 'text.secondary', px: 0 }}
            >
              Letzte Versuche ({attempts.length})
            </Button>
            <Collapse in={attemptsOpen}>
              <Stack spacing={0.5} sx={{ pl: 1, pt: 0.5 }}>
                {attempts.map((attempt, index) => (
                  <Typography key={index} variant='caption' color='text.secondary'>
                    {dayjs(attempt.at).format('DD.MM. HH:mm')} ·{' '}
                    {attempt.outcome === 'reached' ? 'Erreicht' : 'Nicht erreicht'}
                    {attempt.note ? ` · ${attempt.note}` : ''}
                  </Typography>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {canUpdate && (
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {showBook && (
              <Button
                size='small'
                variant='contained'
                disabled={busy}
                onClick={() => onOpenBookDialog(task)}
                sx={{ textTransform: 'none' }}
              >
                Erreicht – Pizza buchen
              </Button>
            )}
            {showNotReached && (
              <Button
                size='small'
                variant='outlined'
                color='warning'
                disabled={busy}
                onClick={() => onOpenNotReachedDialog(task)}
                sx={{ textTransform: 'none' }}
              >
                Nicht erreicht
              </Button>
            )}
            {showInfoMail && (
              <Button
                size='small'
                variant='outlined'
                disabled={busy}
                onClick={() => onSendInfoMail(task)}
                sx={{ textTransform: 'none' }}
                startIcon={busy ? <CircularProgress size={14} /> : null}
              >
                Infomail senden
              </Button>
            )}
            {showClose && (
              <Button
                size='small'
                color='inherit'
                disabled={busy}
                onClick={() => onClose(task)}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Keine Pizza gewünscht
              </Button>
            )}
            {showReopen && (
              <Button
                size='small'
                variant='outlined'
                disabled={busy}
                onClick={() => onReopen(task)}
                sx={{ textTransform: 'none' }}
              >
                Wieder öffnen
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

// A single booking row inside an order window day group.
const PreorderRow = ({ row, canConfirmPending, requestBusy, onConfirmRequest, onRejectRequest }) => {
  const theme = useTheme();
  const pendingRequests = Array.isArray(row.pendingRequests) ? row.pendingRequests : [];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '56px minmax(0, 1fr)', sm: '96px minmax(0, 1fr)' },
        gap: { xs: 1, sm: 2 },
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor:
          row.delta === 'new' || row.delta === 'changed'
            ? alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.08 : 0.16)
            : 'background.paper',
      }}
    >
      <Stack sx={{ pt: 0.25 }}>
        <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {dayjs(row.startTime).format('HH:mm')}
        </Typography>
        {row.plannedServeAt && (
          <Typography variant='caption' color='text.secondary'>
            Serv. {dayjs(row.plannedServeAt).format('HH:mm')}
          </Typography>
        )}
      </Stack>

      <Stack spacing={0.75} sx={{ minWidth: 0 }}>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
          <Typography variant='body1' sx={{ fontWeight: 600, overflowWrap: 'break-word' }}>
            {row.customerName} (#{row.bookingNumber})
          </Typography>
          <Chip size='small' label={`${row.participantCount} Pers.`} />
          {row.isKibu && <Chip size='small' color='secondary' variant='outlined' label='KiBu' />}
          {row.status === 'awaitingConfirmation' && (
            <Chip size='small' color='warning' variant='outlined' label='Buchung unbestätigt' />
          )}
          {row.delta === 'new' && <Chip size='small' color='warning' label='Neu seit Übergabe' />}
          {row.delta === 'changed' && <Chip size='small' color='warning' label='Geändert' />}
        </Stack>

        <Typography variant='body2'>{formatItems(row.items)}</Typography>

        {row.comment && (
          <Typography variant='caption' color='text.secondary'>
            Kommentar: {row.comment}
          </Typography>
        )}
        {row.customerComment && (
          <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
            <Chip size='small' color='info' variant='outlined' label='Kundenwunsch' />
            <Typography variant='caption' color='text.secondary'>
              {row.customerComment}
            </Typography>
          </Stack>
        )}

        {pendingRequests.map(request => (
          <Stack
            key={request.requestId}
            direction='row'
            spacing={1}
            alignItems='center'
            flexWrap='wrap'
            useFlexGap
            sx={{
              p: 1,
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'warning.main',
              backgroundColor: alpha(
                theme.palette.warning.main,
                theme.palette.mode === 'light' ? 0.1 : 0.2
              ),
            }}
          >
            <Chip
              size='small'
              color='warning'
              label={`Pizza-Anfrage: ${request.count}× ${request.upgradeName}${
                request.note ? ` (${request.note})` : ''
              }`}
            />
            {request.requestedAt && (
              <Typography variant='caption' color='text.secondary'>
                angefragt {dayjs(request.requestedAt).format('DD.MM. HH:mm')}
              </Typography>
            )}
            {canConfirmPending && (
              <>
                <Button
                  size='small'
                  variant='contained'
                  color='success'
                  disabled={!!requestBusy[request.requestId]}
                  onClick={() => onConfirmRequest(row.bookingId, request.requestId)}
                  sx={{ textTransform: 'none' }}
                >
                  Bestätigen
                </Button>
                <Button
                  size='small'
                  variant='outlined'
                  color='error'
                  disabled={!!requestBusy[request.requestId]}
                  onClick={() => onRejectRequest(row.bookingId, request.requestId)}
                  sx={{ textTransform: 'none' }}
                >
                  Ablehnen
                </Button>
              </>
            )}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

// One order window ("Aktuelle Liste" / "Nächste Woche") with day groups.
const PreorderWindowSection = ({
  win,
  isCurrent,
  nowTs,
  canConfirmPending,
  requestBusy,
  onConfirmRequest,
  onRejectRequest,
  submitSlot,
}) => {
  const theme = useTheme();
  if (!win) return null;
  const rangeLabel = `${dayjs(win.windowStart).format('DD.MM.YYYY')} – ${dayjs(win.windowEnd)
    .subtract(1, 'day')
    .format('DD.MM.YYYY')}`;
  const days = Array.isArray(win.days) ? win.days : [];
  const removed = Array.isArray(win.removedSinceSubmit) ? win.removedSinceSubmit : [];

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: 'divider' }}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent='space-between'
        >
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              {isCurrent ? 'Aktuelle Liste (an Lieferant übergeben)' : 'Nächste Woche (in Planung)'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {rangeLabel} · {win.totalPizzaCount} Pizzen gesamt
            </Typography>
          </Box>
          <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
            {win.submission ? (
              <Chip
                size='small'
                color='success'
                label={`Übergeben am ${dayjs(win.submission.submittedAt).format(
                  'DD.MM.YYYY HH:mm'
                )} · ${win.submission.totalPizzaCount} Pizzen`}
              />
            ) : (
              <Chip size='small' variant='outlined' label='Noch nicht übergeben' />
            )}
            {submitSlot}
          </Stack>
        </Stack>

        {!isCurrent && win.cutoffAt && (
          <Alert severity='info' icon={false} sx={{ py: 0.25 }}>
            Selbstbuchung läuft noch bis {dayjs(win.cutoffAt).format('dd DD.MM.YYYY HH:mm')} (
            {formatCountdown(win.cutoffAt, nowTs)})
          </Alert>
        )}

        {days.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            Keine Pizza-Buchungen in diesem Zeitraum.
          </Typography>
        ) : (
          days.map(day => (
            <Box key={day.date}>
              <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.75 }}>
                {dayjs(day.date).format('dddd, DD.MM.YYYY')} · {day.pizzaCount}{' '}
                {day.pizzaCount === 1 ? 'Pizza' : 'Pizzen'}
              </Typography>
              <Stack spacing={1}>
                {day.rows.map(row => (
                  <PreorderRow
                    key={row.bookingId}
                    row={row}
                    canConfirmPending={canConfirmPending}
                    requestBusy={requestBusy}
                    onConfirmRequest={onConfirmRequest}
                    onRejectRequest={onRejectRequest}
                  />
                ))}
              </Stack>
            </Box>
          ))
        )}

        {isCurrent && removed.length > 0 && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'error.main',
              backgroundColor: alpha(
                theme.palette.error.main,
                theme.palette.mode === 'light' ? 0.06 : 0.16
              ),
            }}
          >
            <Typography variant='subtitle2' color='error.main' sx={{ fontWeight: 700, mb: 0.5 }}>
              Seit Übergabe entfernt/storniert
            </Typography>
            <Stack spacing={0.5}>
              {removed.map(entry => (
                <Typography key={entry.bookingId} variant='body2' color='error.main'>
                  {dayjs(entry.startTime).format('DD.MM. HH:mm')} · {entry.customerName} (#
                  {entry.bookingNumber}) · {entry.pizzaCount}{' '}
                  {entry.pizzaCount === 1 ? 'Pizza' : 'Pizzen'}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

// Preorder list ("Pizzaliste") of a special-pizza-treatment branch: call tasks
// for KiBu bookings without pizza, the current (submitted) and next (planned)
// weekly order window and the supplier hand-over.
const PizzaPreorderList = ({ branchId }) => {
  const authCtx = useContext(AuthContext);
  const {
    getPizzaPreorder,
    submitPizzaPreorder,
    addPizzaOutreachAttempt,
    sendPizzaOutreachInfoMail,
    bookPizzaOutreach,
    closePizzaOutreach,
    reopenPizzaOutreach,
    confirmPizzaRequest,
    rejectPizzaRequest,
  } = useInternalBooking();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [notActivated, setNotActivated] = useState(false);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [taskBusy, setTaskBusy] = useState({});
  const [requestBusy, setRequestBusy] = useState({});
  const [bookDialogTask, setBookDialogTask] = useState(null);
  const [bookUpgradeId, setBookUpgradeId] = useState('');
  const [bookCount, setBookCount] = useState(1);
  const bookNoteRef = useRef(null);
  const [notReachedDialogTask, setNotReachedDialogTask] = useState(null);
  const notReachedNoteRef = useRef(null);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const canUpdate = authCtx.checkRightAny('internalBooking:update:minor');
  const canConfirmPending = authCtx.checkRightAny('internalBooking:special:confirmpending');
  const canSubmitList = authCtx.checkRightAny('internalBooking:special:submitPizzaList');

  const load = async ({ showLoading = false } = {}) => {
    if (!branchId) return;
    if (showLoading) setIsLoading(true);
    try {
      const response = await getPizzaPreorder({ branchId });
      // Null means the request was stored for auth escalation.
      if (response) {
        setData(response);
        setLoadError(null);
        setNotActivated(false);
      }
    } catch (err) {
      if (
        err?.code === 'BRANCH_NOT_SPECIAL_PIZZA' ||
        err?.code === 'PIZZA_TREATMENT_DISABLED' ||
        err?.statusCode === 503
      ) {
        setNotActivated(true);
      } else {
        setLoadError(err?.message || 'Pizzaliste konnte nicht geladen werden.');
        toast.error('Fehler beim Laden der Pizzaliste');
      }
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!branchId) return undefined;
    setNotActivated(false);
    setLoadError(null);
    load({ showLoading: true });
    const interval = setInterval(() => {
      setNowTs(Date.now());
      load({ showLoading: false });
    }, 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const settings = data?.settings || null;
  const windows = Array.isArray(data?.windows) ? data.windows : [];
  const currentWindow = windows.find(win => win.key === 'current') || null;
  const nextWindow = windows.find(win => win.key === 'next') || null;
  const outreach = Array.isArray(data?.outreach) ? data.outreach : [];
  const pizzaUpsells = Array.isArray(data?.pizzaUpsells) ? data.pizzaUpsells : [];

  const deadlineText = useMemo(() => {
    if (!settings) return null;
    return `Selbstbuchung bis ${formatWeeklyMoment(settings.orderCutoff)} • Prüfung bis ${formatWeeklyMoment(
      settings.reviewDeadline
    )} • Übergabe an Lieferant bis ${formatWeeklyMoment(settings.submitDeadline)}`;
  }, [settings]);

  const tierText = useMemo(() => formatTierInfo(settings?.allIncPizzaTiers), [settings]);

  const customerBase =
    (import.meta && import.meta.env && import.meta.env.VITE_CUSTOMER_FRONTEND_BASE_URL) || '';
  const supplierPath =
    settings?.hasSupplierAccess && settings?.supplierAccessToken && settings?.supplierAccessSecret
      ? `/pizza-list/${settings.supplierAccessToken}/${settings.supplierAccessSecret}`
      : null;
  const supplierUrl = supplierPath && customerBase ? `${customerBase}${supplierPath}` : null;

  const withTaskBusy = async (bookingId, fn) => {
    setTaskBusy(prev => ({ ...prev, [bookingId]: true }));
    try {
      await fn();
    } finally {
      setTaskBusy(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const handleOpenBookDialog = task => {
    setBookUpgradeId(pizzaUpsells[0]?.upgradeId || '');
    setBookCount(1);
    setBookDialogTask(task);
  };

  const handleBookSubmit = async () => {
    if (!bookDialogTask || !bookUpgradeId) return;
    const count = Math.min(20, Math.max(1, Number(bookCount || 1)));
    const note = (bookNoteRef.current?.value || '').trim();
    setActionLoading(true);
    try {
      await bookPizzaOutreach(bookDialogTask.bookingId, {
        upgradeId: bookUpgradeId,
        count,
        note: note || undefined,
      });
      toast.success('Pizza wurde zur Buchung hinzugefügt.');
      setBookDialogTask(null);
      await load();
    } catch (err) {
      toast.error('Fehler beim Buchen der Pizza');
    } finally {
      setActionLoading(false);
    }
  };

  const handleNotReachedSubmit = async () => {
    if (!notReachedDialogTask) return;
    const note = (notReachedNoteRef.current?.value || '').trim();
    setActionLoading(true);
    try {
      await addPizzaOutreachAttempt(notReachedDialogTask.bookingId, {
        outcome: 'notReached',
        note: note || undefined,
      });
      toast.success('Versuch gespeichert.');
      setNotReachedDialogTask(null);
      await load();
    } catch (err) {
      toast.error('Fehler beim Speichern des Versuchs');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendInfoMail = task =>
    withTaskBusy(task.bookingId, async () => {
      try {
        await sendPizzaOutreachInfoMail(task.bookingId);
        toast.success('Infomail wurde versendet.');
        await load();
      } catch (err) {
        if (err?.code === 'CUSTOMER_MAIL_NOT_ALLOWED') {
          toast.error('Für diese Buchung sind Kundenmails deaktiviert.');
        } else {
          toast.error('Fehler beim Senden der Infomail');
        }
      }
    });

  const handleCloseOutreach = task =>
    withTaskBusy(task.bookingId, async () => {
      try {
        await closePizzaOutreach(task.bookingId);
        toast.success('Aufgabe geschlossen.');
        await load();
      } catch (err) {
        toast.error('Fehler beim Schließen der Aufgabe');
      }
    });

  const handleReopenOutreach = task =>
    withTaskBusy(task.bookingId, async () => {
      try {
        await reopenPizzaOutreach(task.bookingId);
        toast.success('Aufgabe wieder geöffnet.');
        await load();
      } catch (err) {
        toast.error('Fehler beim Öffnen der Aufgabe');
      }
    });

  const handleConfirmRequest = async (bookingId, requestId) => {
    setRequestBusy(prev => ({ ...prev, [requestId]: true }));
    try {
      await confirmPizzaRequest(bookingId, requestId);
      toast.success('Pizza-Anfrage bestätigt.');
      await load();
    } catch (err) {
      toast.error('Fehler beim Bestätigen der Pizza-Anfrage');
    } finally {
      setRequestBusy(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleRejectRequest = async (bookingId, requestId) => {
    setRequestBusy(prev => ({ ...prev, [requestId]: true }));
    try {
      await rejectPizzaRequest(bookingId, requestId);
      toast.success('Pizza-Anfrage abgelehnt.');
      await load();
    } catch (err) {
      toast.error('Fehler beim Ablehnen der Pizza-Anfrage');
    } finally {
      setRequestBusy(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleSubmitList = async () => {
    setActionLoading(true);
    try {
      await submitPizzaPreorder({ branchId, windowKey: 'next' });
      toast.success('Pizzaliste wurde übergeben.');
      setSubmitDialogOpen(false);
      await load();
    } catch (err) {
      toast.error('Fehler beim Übergeben der Pizzaliste');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopySupplierLink = async () => {
    const value = supplierUrl || supplierPath;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Link kopiert');
    } catch (err) {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  if (notActivated) {
    return (
      <Alert severity='warning'>Für diesen Standort ist die Pizzaliste nicht aktiviert.</Alert>
    );
  }

  if (isLoading && !data) {
    return <PreorderSkeleton />;
  }

  if (loadError && !data) {
    return <Alert severity='error'>{loadError}</Alert>;
  }

  const supplierEmailMissing = !settings?.supplierEmail;
  const submitButton = canSubmitList ? (
    <Tooltip title={supplierEmailMissing ? 'Keine Lieferanten-E-Mail am Standort hinterlegt' : ''}>
      <span>
        <Button
          size='small'
          variant='contained'
          disabled={supplierEmailMissing || actionLoading}
          onClick={() => setSubmitDialogOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          {nextWindow?.submission ? 'Erneut übergeben' : 'Liste an Lieferant übergeben'}
        </Button>
      </span>
    </Tooltip>
  ) : null;

  return (
    <Stack spacing={{ xs: 2, sm: 3 }}>
      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={0.75}>
          <Typography variant='h6' sx={{ fontWeight: 700 }}>
            Pizzaliste {data?.branch?.locationName ? `· ${data.branch.locationName}` : ''}
          </Typography>
          {deadlineText && (
            <Typography variant='body2' color='text.secondary'>
              {deadlineText}
            </Typography>
          )}
          {tierText && (
            <Typography variant='body2' color='text.secondary'>
              All-Inc-Staffel: {tierText}
            </Typography>
          )}
          {supplierPath && (
            <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
              {supplierUrl ? (
                <Link
                  href={supplierUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  underline='hover'
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  Live-Ansicht für Lieferant
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </Link>
              ) : (
                <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
                  Live-Ansicht für Lieferant: {supplierPath}
                </Typography>
              )}
              <Tooltip title='Link kopieren'>
                <IconButton size='small' onClick={handleCopySupplierLink}>
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={1.5}>
          <Typography variant='h6' sx={{ fontWeight: 700 }}>
            Anrufen: Kindergeburtstage ohne Pizza
          </Typography>
          {outreach.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Keine offenen Anruf-Aufgaben.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {outreach.map(task => (
                <OutreachTaskCard
                  key={task.bookingId}
                  task={task}
                  canUpdate={canUpdate}
                  busy={!!taskBusy[task.bookingId]}
                  onOpenBookDialog={handleOpenBookDialog}
                  onOpenNotReachedDialog={setNotReachedDialogTask}
                  onSendInfoMail={handleSendInfoMail}
                  onClose={handleCloseOutreach}
                  onReopen={handleReopenOutreach}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      <PreorderWindowSection
        win={currentWindow}
        isCurrent
        nowTs={nowTs}
        canConfirmPending={canConfirmPending}
        requestBusy={requestBusy}
        onConfirmRequest={handleConfirmRequest}
        onRejectRequest={handleRejectRequest}
        submitSlot={null}
      />

      <PreorderWindowSection
        win={nextWindow}
        isCurrent={false}
        nowTs={nowTs}
        canConfirmPending={canConfirmPending}
        requestBusy={requestBusy}
        onConfirmRequest={handleConfirmRequest}
        onRejectRequest={handleRejectRequest}
        submitSlot={submitButton}
      />

      {/* Dialog: book a pizza after a successful call */}
      <Dialog
        open={!!bookDialogTask}
        onClose={() => {
          if (!actionLoading) setBookDialogTask(null);
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Pizza buchen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {bookDialogTask && (
              <Typography variant='body2' color='text.secondary'>
                {bookDialogTask.customerName} (#{bookDialogTask.bookingNumber}) ·{' '}
                {dayjs(bookDialogTask.startTime).format('dd DD.MM. HH:mm')}
              </Typography>
            )}
            <FormControl fullWidth size='small' disabled={actionLoading || pizzaUpsells.length === 0}>
              <InputLabel id='pizza-outreach-upgrade-label'>Pizza</InputLabel>
              <Select
                labelId='pizza-outreach-upgrade-label'
                label='Pizza'
                value={bookUpgradeId}
                onChange={event => setBookUpgradeId(event.target.value)}
              >
                {pizzaUpsells.map(upgrade => (
                  <MenuItem key={upgrade.upgradeId} value={upgrade.upgradeId}>
                    {upgrade.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='Anzahl'
              value={bookCount}
              onChange={event => setBookCount(event.target.value)}
              inputProps={{ min: 1, max: 20 }}
              disabled={actionLoading}
            />
            <TextField
              key={bookDialogTask?.bookingId || 'book-note'}
              fullWidth
              size='small'
              label='Anmerkung'
              placeholder='z. B. halb/halb'
              defaultValue=''
              inputRef={bookNoteRef}
              inputProps={{ maxLength: 280 }}
              disabled={actionLoading}
            />
            {pizzaUpsells.length === 0 && (
              <Alert severity='info'>Aktuell sind keine Pizza-Artikel auswählbar.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookDialogTask(null)} disabled={actionLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleBookSubmit}
            variant='contained'
            disabled={actionLoading || !bookUpgradeId}
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            Pizza buchen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: log a "not reached" call attempt */}
      <Dialog
        open={!!notReachedDialogTask}
        onClose={() => {
          if (!actionLoading) setNotReachedDialogTask(null);
        }}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Nicht erreicht</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Der Versuch wird protokolliert. Danach kann direkt die Infomail an den Kunden
              gesendet werden.
            </Typography>
            <TextField
              key={notReachedDialogTask?.bookingId || 'not-reached-note'}
              fullWidth
              size='small'
              label='Notiz (optional)'
              defaultValue=''
              inputRef={notReachedNoteRef}
              inputProps={{ maxLength: 280 }}
              disabled={actionLoading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotReachedDialogTask(null)} disabled={actionLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleNotReachedSubmit}
            variant='contained'
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: confirm the supplier hand-over */}
      <Dialog
        open={submitDialogOpen}
        onClose={() => {
          if (!actionLoading) setSubmitDialogOpen(false);
        }}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Liste an Lieferant übergeben</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant='body2'>
              Die Liste der Woche{' '}
              {nextWindow
                ? `${dayjs(nextWindow.windowStart).format('DD.MM.YYYY')} – ${dayjs(
                    nextWindow.windowEnd
                  )
                    .subtract(1, 'day')
                    .format('DD.MM.YYYY')}`
                : ''}{' '}
              mit insgesamt {nextWindow?.totalPizzaCount ?? 0}{' '}
              {(nextWindow?.totalPizzaCount ?? 0) === 1 ? 'Pizza' : 'Pizzen'} wird an{' '}
              {settings?.supplierEmail || '–'} gesendet.
            </Typography>
            {nextWindow?.submission && (
              <Alert severity='info' icon={false} sx={{ py: 0.25 }}>
                Diese Woche wurde bereits am{' '}
                {dayjs(nextWindow.submission.submittedAt).format('DD.MM.YYYY HH:mm')} übergeben und
                wird erneut gesendet.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialogOpen(false)} disabled={actionLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmitList}
            variant='contained'
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            Übergeben
          </Button>
        </DialogActions>
      </Dialog>

    </Stack>
  );
};

export default PizzaPreorderList;
